import { useEffect, useMemo, useState } from "react";
import { Dropdown, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import { PAGE_SIZE_DEFAULT } from "../../../constants";
import { getCurrencySymbol } from "../../../helpers/utils";
import { getCampPositions } from "../../../services/campPositionServices";
import { CampPosition } from "../../../types/campPosition";
import { UserType } from "../../../types/user";
import ErrorPage from "../../ErrorPage";
import PageLoader from "../../PageLoader";
import PaginationComponent from "../../Pagination";

import styles from "./index.module.scss";

const sizeOfPage = PAGE_SIZE_DEFAULT;
const POSITION_COLUMN_COUNT = 10;

const SLICE_COLORS = [
  "#e42082",
  "#701d1b",
  "#914507",
  "#ba894f",
  "#3d1912",
  "#009761",
  "#4e5258",
  "#6e7177",
];

type PortfolioOption = { key: string; value: string };

const getPortfolioOptions = (appCountry?: string): PortfolioOption[] => {
  switch (appCountry) {
    case "HK":
      return [
        { key: "CORE-HKD", value: "HKD portfolio" },
        { key: "CORE-USD", value: "USD portfolio" },
      ];
    case "AE":
      return [
        { key: "CORE-AED", value: "AED portfolio" },
        { key: "CORE-USD", value: "USD portfolio" },
      ];
    default:
      return [
        { key: "CORE-SGD", value: "SGD portfolio" },
        { key: "CORE-USD", value: "USD portfolio" },
        { key: "CORE-BIZ-SGD", value: "Business SGD portfolio" },
      ];
  }
};

const positionLabel = (position: CampPosition) =>
  position.shortName || position.name || position.securityISIN;

const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/;

type DecimalParts = {
  negative: boolean;
  whole: string;
  fraction: string;
};

const parseDecimal = (value: string): DecimalParts | null => {
  const match = DECIMAL_PATTERN.exec(value.trim());
  if (!match) return null;
  const fraction = match[3] ?? "";
  const whole = match[2].replace(/^0+(?=\d)/, "") || "0";
  const negative =
    match[1] === "-" && !(whole === "0" && /^0*$/.test(fraction));
  return { negative, whole, fraction };
};

const groupInteger = (digits: string) =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatDecimalString = (value: string): string | null => {
  const parsed = parseDecimal(value);
  if (!parsed) return null;
  const fraction = parsed.fraction.replace(/0+$/, "");
  const body = fraction
    ? `${groupInteger(parsed.whole)}.${fraction}`
    : groupInteger(parsed.whole);
  return parsed.negative ? `-${body}` : body;
};

const addDecimalStrings = (left: string, right: string): string | null => {
  const a = parseDecimal(left);
  const b = parseDecimal(right);
  if (!a || !b) return null;

  const scale = Math.max(a.fraction.length, b.fraction.length);
  const toScaled = (parts: DecimalParts) => {
    const magnitude = BigInt(
      `${parts.whole}${parts.fraction.padEnd(scale, "0")}`
    );
    return parts.negative ? -magnitude : magnitude;
  };
  const sum = toScaled(a) + toScaled(b);
  const zero = BigInt(0);
  if (sum === zero) return "0";

  const negative = sum < zero;
  const digits = (negative ? -sum : sum).toString().padStart(scale + 1, "0");
  const whole =
    (scale === 0 ? digits : digits.slice(0, -scale)).replace(/^0+(?=\d)/, "") ||
    "0";
  const fraction = scale === 0 ? "" : digits.slice(-scale);
  const body = fraction ? `${whole}.${fraction}` : whole;
  return negative ? `-${body}` : body;
};

const sumDecimalStrings = (values: readonly string[]): string | null =>
  values.reduce<string | null>((total, value) => {
    if (total === null) return null;
    return addDecimalStrings(total, value);
  }, "0");

const formatQuantity = (value: string) => formatDecimalString(value) ?? value;

const formatMoney = (currency: string, value?: string) => {
  if (value === undefined || value === "") return "—";
  const formatted = formatDecimalString(value);
  if (!formatted) return value;
  const symbol = getCurrencySymbol(currency) || "";
  return formatted.startsWith("-")
    ? `-${symbol}${formatted.slice(1)}`
    : `${symbol}${formatted}`;
};

const formatWeight = (weight: string) => {
  const value = Number(weight);
  if (Number.isNaN(value)) return weight;
  return `${value.toFixed(2)}%`;
};

type Slice = {
  key: string;
  label: string;
  value: number;
  percentLabel: string;
  color: string;
};

const buildSlices = (positions: CampPosition[]): Slice[] => {
  const withWeight = positions
    .map((position) => ({
      key: position.securityISIN,
      label: positionLabel(position),
      value: Math.abs(Number(position.weight)) || 0,
      percentLabel: formatWeight(position.weight),
    }))
    .filter((slice) => slice.value > 0);

  const source =
    withWeight.length > 0
      ? withWeight
      : positions
          .map((position) => ({
            key: position.securityISIN,
            label: positionLabel(position),
            value: Math.abs(Number(position.amount)) || 0,
            percentLabel: "",
          }))
          .filter((slice) => slice.value > 0);

  const total = source.reduce((sum, slice) => sum + slice.value, 0);

  return source.map((slice, index) => ({
    ...slice,
    percentLabel:
      slice.percentLabel ||
      (total > 0 ? `${((slice.value / total) * 100).toFixed(2)}%` : "0.00%"),
    color: SLICE_COLORS[index % SLICE_COLORS.length],
  }));
};

const polar = (cx: number, cy: number, radius: number, angle: number) => {
  const radians = ((angle - 90) * Math.PI) / 180;
  return [cx + radius * Math.cos(radians), cy + radius * Math.sin(radians)];
};

const describeArc = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const [startX, startY] = polar(cx, cy, radius, endAngle);
  const [endX, endY] = polar(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 0 ${endX} ${endY} Z`;
};

const HoldingsPie = ({ slices }: { slices: Slice[] }) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  if (total <= 0) return null;

  if (slices.length === 1) {
    return (
      <svg
        className={styles.chart}
        viewBox="0 0 100 100"
        role="img"
        aria-label="Holdings allocation"
      >
        <circle cx="50" cy="50" r="48" fill={slices[0].color} />
      </svg>
    );
  }

  let angle = 0;
  return (
    <svg
      className={styles.chart}
      viewBox="0 0 100 100"
      role="img"
      aria-label="Holdings allocation"
    >
      {slices.map((slice) => {
        const sweep = (slice.value / total) * 360;
        const path = describeArc(50, 50, 48, angle, angle + sweep);
        angle += sweep;
        return <path key={slice.key} d={path} fill={slice.color} />;
      })}
    </svg>
  );
};

interface PositionsTableProps {
  userData?: UserType;
}

export default function PositionsTable({ userData }: PositionsTableProps) {
  const { userId } = useParams();
  const portfolioOptions = useMemo(
    () => getPortfolioOptions(userData?.app_country),
    [userData?.app_country]
  );
  const [selectedType, setSelectedType] = useState(portfolioOptions[0].key);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setSelectedType(portfolioOptions[0].key);
  }, [portfolioOptions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType]);

  const { isLoading, error, data } = useQuery<CampPosition[], Error>(
    ["get-user-positions", userId, selectedType],
    () => getCampPositions(userId || "", selectedType),
    { enabled: !!userId }
  );

  const positions = useMemo(() => data ?? [], [data]);
  const slices = useMemo(() => buildSlices(positions), [positions]);
  const paginatedPositions = useMemo(() => {
    if (!positions.length) return [];
    return positions.slice(
      (currentPage - 1) * sizeOfPage,
      currentPage * sizeOfPage
    );
  }, [currentPage, positions]);

  const totalLabel = useMemo(() => {
    if (!positions.length) return "";
    const currency = positions[0].currency;
    const total = sumDecimalStrings(
      positions.map((position) => position.amount)
    );
    if (total === null) return "—";
    return formatMoney(currency, total);
  }, [positions]);

  if (isLoading) {
    return <PageLoader isLoading={isLoading} content="Loading positions.." />;
  }

  if (error || !data) return <ErrorPage />;

  return (
    <div>
      <div className={styles.boxExternalPagination}>
        <div className={styles.titleContainer}>
          <span className={styles.listGroupTitle}>Positions</span>
          <Dropdown className="add-filters-dropdown m-0">
            <Dropdown.Toggle
              variant="primary"
              className="search-toggle"
              id="positions-portfolio-dropdown"
              data-testid="positions-portfolio-dropdown"
            >
              <span>
                {
                  portfolioOptions.find((option) => option.key === selectedType)
                    ?.value
                }
              </span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {portfolioOptions.map((option) => (
                <Dropdown.Item
                  className={styles.dropdownItem}
                  key={option.key}
                  data-testid={`positions-portfolio-${option.key}`}
                  onClick={() => setSelectedType(option.key)}
                >
                  {option.value}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <PaginationComponent
          pageCount={Math.ceil(positions.length / sizeOfPage)}
          currentPage={currentPage}
          itemOfPage={paginatedPositions.length}
          totalItems={positions.length}
          setCurrentPage={setCurrentPage}
        />
      </div>
      {slices.length > 0 && (
        <div className={styles.summary}>
          <HoldingsPie slices={slices} />
          <div className={styles.legend}>
            {slices.map((slice) => (
              <div className={styles.legendItem} key={slice.key}>
                <span
                  className={styles.swatch}
                  style={{ backgroundColor: slice.color }}
                />
                <span className={styles.legendLabel}>{slice.label}</span>
                <span>{slice.percentLabel}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {totalLabel && (
        <p className={styles.total}>Total market value {totalLabel}</p>
      )}
      <div className={styles.table}>
        <Table hover bordered responsive="md">
          <thead>
            <tr>
              <th>Fund</th>
              <th>ISIN</th>
              <th>Market price</th>
              <th>Settled units</th>
              <th>Unsettled units</th>
              <th>Unconfirmed units</th>
              <th>Amount</th>
              <th>Settled amount</th>
              <th>Weight</th>
              <th>Unrealised gain</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPositions.length > 0 ? (
              paginatedPositions.map((position) => (
                <tr key={position.securityISIN}>
                  <td>{positionLabel(position)}</td>
                  <td>{position.securityISIN}</td>
                  <td>
                    {formatMoney(position.currency, position.marketPrice)}
                  </td>
                  <td>{formatQuantity(position.settledUnits)}</td>
                  <td>{formatQuantity(position.unsettledUnits)}</td>
                  <td>{formatQuantity(position.unconfirmedUnits)}</td>
                  <td>{formatMoney(position.currency, position.amount)}</td>
                  <td>
                    {formatMoney(position.currency, position.settledAmount)}
                  </td>
                  <td>{formatWeight(position.weight)}</td>
                  <td>
                    {formatMoney(position.currency, position.unrealisedGain)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={POSITION_COLUMN_COUNT}
                  className={styles.emptyTable}
                >
                  Oops, no results were found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
